import { Component, type ReactNode } from "react";

const RELOAD_KEY = "chunk-reload-ts";
const COOLDOWN_MS = 10_000;

const CHUNK_ERROR =
  /dynamically imported module|imported module script failed|Failed to fetch dynamically imported module|ChunkLoadError|Loading chunk|error loading dynamically imported module/i;

interface Props {
  children: ReactNode;
}
interface State {
  failed: boolean;
}

/**
 * Recovers from lazy-chunk load failures. After a redeploy the hashed chunk
 * filenames change, so a client holding the old index can request a chunk that
 * no longer exists — the dynamic import rejects and, because <Suspense> does not
 * catch errors, the route would render nothing (blank #root). This boundary
 * reloads once (guarded by a short cooldown so a genuinely broken build cannot
 * loop) to pick up the fresh asset manifest.
 */
class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    if (!CHUNK_ERROR.test(message)) return;
    try {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      if (Date.now() - last > COOLDOWN_MS) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
      }
    } catch {
      window.location.reload();
    }
  }

  render() {
    if (this.state.failed) {
      return (
        <div
          role="alert"
          className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center bg-background text-foreground"
        >
          <p className="text-lg font-semibold">This page is updating…</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-primary px-5 py-2.5 text-primary-foreground font-semibold"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
